import { useMemo } from "react";
import {
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Flex, Text } from "@chakra-ui/react";

import { DocumentType, gql } from "__generated__/gql";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import {
  BrandRetailerFilter,
  BrandRetailersOrderBy,
} from "__generated__/gql/graphql";
import { formatJustifiedDateTimeCell } from "components/Shared/ChakraReactTable/formatters";
import { useAdminQuery } from "utils/apollo/hooks/admin";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import FullPageTable, {
  FREESTANDING_TABLE_CONTAINER_PROPS,
  useFullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import OpenBrandAccountButton from "components/Shared/OpenBrandAccountButton";
import { brandRetailerPage } from "utils/links";
import { assertPresence } from "utils/assertions";

export const query = gql(/* GraphQL */ `
  query AdminRetailerBrandsTable(
    $filter: BrandRetailerFilter
    $orderBy: [BrandRetailersOrderBy!] = BRAND__NAME_ASC
    $limit: Int = 10
    $offset: Int = 0
  ) {
    brandRetailers(
      filter: $filter
      orderBy: $orderBy
      first: $limit
      offset: $offset
    ) {
      totalCount
      nodes {
        id
        createdAt
        retailerId
        brand {
          id
          name
        }
      }
    }
  }
`);

type Datum = NonNullable<
  DocumentType<typeof query>["brandRetailers"]
>["nodes"][number];

const col = createColumnHelper<Datum>();

const columns = [
  col.accessor((r) => r.brand?.name, {
    id: "brandName",
    header: "Brand Name",
    cell: (cell) => {
      const brandName = cell.getValue();
      const datum = cell.row.original;
      assertPresence(datum.brand);
      assertPresence(datum.retailerId);
      return (
        <Flex alignItems="center" gap={4}>
          {brandName}{" "}
          <OpenBrandAccountButton
            brandId={datum.brand.id}
            href={brandRetailerPage(datum.retailerId)}
            size="xs"
            variant="outline"
            colorScheme="lightPinkButton"
            flex="0 0 auto"
            width="auto"
            px=".75em"
            ml="auto"
          >
            Open
          </OpenBrandAccountButton>
        </Flex>
      );
    },
    size: 150,
  }),
  col.accessor("createdAt", {
    header: "Added",
    cell: formatJustifiedDateTimeCell,
    size: 125,
    meta: { props: { textAlign: "center" } },
  }),
];

const columnPinning: ColumnPinningState = { left: ["brandName"] };

export const orderByEnumsById: GQLOrderByEnumsById<BrandRetailersOrderBy> = {
  brandName: [
    BrandRetailersOrderBy.BRAND__NAME_ASC,
    BrandRetailersOrderBy.BRAND__NAME_DESC,
  ],
  createdAt: [
    BrandRetailersOrderBy.CREATED_AT_ASC,
    BrandRetailersOrderBy.CREATED_AT_DESC,
  ],
};

export type TableProps = {
  retailerId: UUID | null | undefined;
};

export default function AdminRetailerBrandsTable({ retailerId }: TableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: 50 });

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "brandName", desc: false }],
  );

  const filter = useMemo<BrandRetailerFilter>(
    () => ({ retailerId: { equalTo: retailerId } }),
    [retailerId],
  );

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data,
    loading: isLoading,
    refetch,
  } = useAdminQuery(query, {
    skip: !retailerId || isPendingFilterReset || isPendingSortingReset,
    variables: { filter, orderBy, ...limitOffset },
    fetchPolicy: "cache-and-network",
  });

  const { nodes, totalCount } = data?.brandRetailers || {};

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(nodes),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const tableProps = useFullPageTableProps<Datum>([
    useFixedLayout(tableInstance),
    { tableProps: { variant: "condensed" } },
  ]);

  return (
    <FullPageTable
      {...{ isLoading, tableInstance, totalCount }}
      {...tableProps}
      resultTypeLabel="brands"
      noRowsPlaceholder={<Text>No brands found</Text>}
      onRefresh={refetch}
      {...FREESTANDING_TABLE_CONTAINER_PROPS}
      scrollContainerProps={{
        ...FREESTANDING_TABLE_CONTAINER_PROPS.scrollContainerProps,
        maxHeight: undefined,
        minHeight: undefined,
        bg: "gray.50",
      }}
    />
  );
}
